import { useCallback, useMemo } from 'react';

import { matchSorter } from 'match-sorter';

import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';

import { IdAutocomplete } from '../../../components/IdAutocomplete';
import type { IdOption } from '../../../components/IdAutocomplete';

import { useNafEntriesQuery } from '../hooks/useNafEntriesQuery.hook';

interface NafIdOption extends IdOption<string> {
  readonly code: string;
  readonly designation: string;
}

interface Props {
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly helperText?: string;
  readonly label?: string;
  readonly name?: string;
  readonly onBlur?: () => void;
  readonly onChange: (nafId: string | null) => void;
  readonly required?: boolean;
  readonly value?: string | null;
}

export const NafEntryAutocomplete = ({
  disabled = false,
  error = false,
  helperText,
  label = 'NAF (APE)',
  name,
  onBlur,
  onChange,
  required = false,
  value,
}: Readonly<Props>) => {
  const { data, isLoading } = useNafEntriesQuery();

  const nafEntries = data?.findAllActiveNafEntries ?? EMPTY_ARRAY;

  const options = useMemo<readonly NafIdOption[]>(
    () =>
      nafEntries.map((nafEntry) => ({
        code: nafEntry.code,
        designation: nafEntry.designation,
        id: nafEntry._id,
        label: `${nafEntry.code} - ${nafEntry.designation} (${nafEntry.version})`,
      })),
    [nafEntries],
  );

  const filterOptions = useCallback(
    (
      nafIdOptions: readonly NafIdOption[],
      { inputValue }: Readonly<{ inputValue: string }>,
    ) =>
      matchSorter(nafIdOptions, inputValue, { keys: ['code', 'designation'] }),
    [],
  );

  const handleChange = useCallback(
    (nafId: string) => {
      onChange(nafId || EMPTY_STRING);
    },
    [onChange],
  );

  return (
    <IdAutocomplete<string, NafIdOption>
      disabled={disabled || isLoading}
      error={error}
      filterOptions={filterOptions}
      helperText={helperText}
      label={label}
      loading={isLoading}
      loadingText="Chargement ..."
      name={name}
      noOptionsText="Aucun NAF trouvé"
      onBlur={onBlur}
      onChange={handleChange}
      options={options}
      required={required}
      value={value ?? EMPTY_STRING}
    />
  );
};
